import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Product } from '../types/api';
import { calculateCartItemFees, GST_RATE, PST_RATE } from '../utils/pricing';

interface Attribute {
  name: string;
  value: any;
  id?: number | string;
}

export interface CartItem {
  id: string;
  productId: number | string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  sku?: string;
  description?: string;
  weight?: number;
  product?: Product;
  attributes?: Attribute[];
}

interface CartContextType {
  items: CartItem[];
  addToCart: (product: Product, quantity?: number) => void;
  removeFromCart: (itemId: string) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  clearCart: () => void;
  getItemById: (productId: number | string) => CartItem | undefined;
  getCartItem: (productId: number | string) => CartItem | undefined;
  subtotal: number;
  itemCount: number;
  tax: number;
  deposit: number;
  ecoFee: number;
  deliveryFee: number;
  total: number;
  gst: number;
  pst: number;
}

const CartContext = createContext<CartContextType | null>(null);

// Storage key constant
const CART_STORAGE_KEY = 'natural_source_cart';

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

// Function to load cart data from localStorage
const loadCartFromStorage = (): CartItem[] => {
  try {
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    if (savedCart) {
      return JSON.parse(savedCart);
    }
  } catch (e) {
    console.error('Failed to parse saved cart:', e);
    localStorage.removeItem(CART_STORAGE_KEY);
  }
  return [];
};

// Function to save cart data to localStorage
const saveCartToStorage = (items: CartItem[]): void => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  } catch (e) {
    console.error('Failed to save cart to localStorage:', e);
  }
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize with data from localStorage
  const [items, setItems] = useState<CartItem[]>(loadCartFromStorage());
  
  // Save cart to localStorage whenever items change
  useEffect(() => {
    saveCartToStorage(items);
  }, [items]);
  
  const addToCart = (product: Product | any, quantity: number = 1) => {
    // Create a consistent ID
    const productId = String(product.id);
    
    // Check if item is already in cart
    const existingItemIndex = items.findIndex(item => 
      item.id === productId || 
      (item.productId && item.productId.toString() === productId)
    );
    
    if (existingItemIndex >= 0) {
      // Update existing item
      const updatedItems = [...items];
      updatedItems[existingItemIndex].quantity += quantity;
      setItems(updatedItems);
      toast.success(`Updated quantity for ${product.name}`, { 
        position: 'bottom-right'
      });
    } else {
      // Add new item
      const newItem: CartItem = {
        id: productId,
        productId: product.id,
        name: product.name,
        price: product.price,
        quantity,
        image: product.imageUrl || product.thumbnailUrl,
        sku: product.sku,
        description: product.description,
        weight: product.weight,
        product: product, // Store the full product object
        attributes: product.attributes
      };
      
      setItems(prevItems => [...prevItems, newItem]);
      toast.success(`Added ${product.name} to cart`, { 
        position: 'bottom-right'
      });
    }
  };
  
  const removeFromCart = (itemId: string) => {
    const item = items.find(item => item.id === itemId);
    if (item) {
      setItems(items.filter(item => item.id !== itemId));
      toast.success('Item removed from cart', { 
        position: 'bottom-right'
      });
    }
  };
  
  const updateQuantity = (itemId: string, quantity: number) => {
    // If quantity is 0 or less, remove the item
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    
    // Otherwise update the quantity
    const updatedItems = items.map(item => 
      item.id === itemId ? { ...item, quantity } : item
    );
    
    setItems(updatedItems);
  };
  
  const clearCart = () => {
    setItems([]);
    toast.success('Cart cleared', { 
      position: 'bottom-right'
    });
  };
  
  const getItemById = (productId: number | string) => {
    const id = String(productId);
    return items.find(item => 
      item.id === id || 
      String(item.productId) === id
    );
  };
  
  // Calculate cart totals using our pricing utility
  const calculateCartTotals = () => {
    let subtotal = 0;
    let tax = 0;
    let gst = 0;
    let pst = 0;
    let deposit = 0;
    let ecoFee = 0;
    let total = 0;
    let count = 0;
    
    items.forEach(item => {
      const fees = calculateCartItemFees(item);
      subtotal += fees.subtotal;
      tax += fees.tax;
      gst += fees.gst;
      pst += fees.pst;
      deposit += fees.totalDeposit;
      ecoFee += fees.totalEcoFee;
      total += fees.total;
      count += item.quantity;
    });
    
    // Calculate delivery fee - $9.99 flat fee for orders under $100
    const deliveryFee = subtotal < 100 ? 9.99 : 0;
    
    // Add delivery fee to total
    total += deliveryFee;
    
    // Round all values to ensure consistency
    return {
      subtotal: Number(subtotal.toFixed(2)),
      tax: Number(tax.toFixed(2)),
      gst: Number(gst.toFixed(2)),
      pst: Number(pst.toFixed(2)),
      deposit: Number(deposit.toFixed(2)),
      ecoFee: Number(ecoFee.toFixed(2)),
      deliveryFee: Number(deliveryFee.toFixed(2)),
      total: Number(total.toFixed(2)),
      itemCount: count
    };
  };
  
  const {
    subtotal,
    tax,
    gst,
    pst,
    deposit,
    ecoFee,
    deliveryFee,
    total,
    itemCount
  } = calculateCartTotals();
  
  const value = {
    items,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getItemById,
    getCartItem: getItemById,
    subtotal,
    itemCount,
    tax,
    gst,
    pst,
    deposit,
    ecoFee,
    deliveryFee,
    total
  };
  
  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
}; 